import { ReactNode, useCallback, useEffect, useState } from "react";
import { Check, Loader } from "lucide-react";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Paginator } from "primereact/paginator";
import SearchInput from "@/components/common/SearchInput";
import templateApi from "@/api/template";
import { Name, FunctionalId } from "./Details";
import { Layer, TemplateType } from "@/interfaces/template.interface";

interface Filter {
  templateId?: string;
}

interface Props {
  filter?: Filter;
  header?: ReactNode;
  onSelect?: (items: Layer[]) => void;
}

const LAYER_TYPE_OPTIONS: { label: string; value: TemplateType }[] = [
  { label: "Print", value: TemplateType.print },
  { label: "Preview", value: TemplateType.preview },
];

export default function LayerList({ filter, header, onSelect }: Props) {
  const [itemList, setItemList] = useState<Layer[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(20);
  const [totalItems, setTotalItems] = useState(0);
  const [selected, setSelected] = useState<Layer[]>([]);
  const [layerType, setLayerType] = useState<TemplateType>(TemplateType.print);

  const onPageChange = (event: any) => {
    setPage(event.page + 1);
    setSize(event.rows);
  };

  const onChangeLayerType = (type: TemplateType) => {
    setLayerType(type);
    setPage(1);
    setSelected([]);
  };

  const getItemList = useCallback(() => {
    const templateId = filter?.templateId;
    if (!templateId) {
      setItemList([]);
      setTotalItems(0);
      return;
    }

    setLoading(true);
    templateApi
      .readManyLayersById(templateId, layerType, {
        page,
        size,
        search,
        sort: "zIndex",
      })
      .then((data) => {
        setItemList(data.data);
        const pagination = data.pagination;
        if (pagination) setTotalItems(pagination.totalItems);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [filter, layerType, search, page, size]);

  useEffect(() => {
    getItemList();
  }, [getItemList]);

  return (
    <>
      <div className="flex h-full flex-col overflow-auto">
        <div className="flex items-center justify-between gap-4 border-b-1 border-gray-300 p-4">
          <div className="flex flex-1 items-center gap-2">
            <span className="text-md truncate font-bold">{header}</span>
          </div>
          {loading && <Loader className="animate-spin" size={20} />}
          <div className="flex items-center gap-2">
            <select
              className="rounded border border-gray-300 px-1 py-1 text-md text-gray-700"
              value={layerType}
              onChange={(e) =>
                onChangeLayerType(e.target.value as TemplateType)
              }
            >
              {LAYER_TYPE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <SearchInput
              onChange={() => setLoading(true)}
              onSearch={setSearch}
            />
          </div>
        </div>
        <DataTable
          rowHover
          className="data-table h-full overflow-auto"
          value={itemList}
          size="small"
          scrollable
          selectionMode="multiple"
          selection={selected as any}
          onSelectionChange={(e: any) => setSelected(e.value)}
        >
          <Column headerClassName="w-10" selectionMode="multiple"></Column>
          <Column
            field="name"
            header="Name"
            body={(item) => <Name data={item} />}
          ></Column>
          <Column
            headerClassName="w-20"
            field="functionalId"
            header="ID"
            frozen
            alignFrozen="right"
            body={(item) => <FunctionalId data={item} />}
          ></Column>
        </DataTable>
        {selected instanceof Array && selected.length > 0 && (
          <div className="flex items-center justify-end gap-4 border-t-1 border-gray-300 p-4">
            <span>{selected.length} selected</span>
            <Button
              severity="info"
              style={{
                height: 36,
                margin: 0,
                padding: "6px 10px",
                gap: 4,
              }}
              label="Select"
              icon={<Check size={18} />}
              onClick={() => onSelect?.(selected)}
            />
          </div>
        )}
        <div className="pagination flex w-full justify-end border-t-1 border-gray-300">
          <Paginator
            first={size * (page - 1)}
            rows={size}
            totalRecords={totalItems}
            // rowsPerPageOptions={[10, 20, 50]}
            onPageChange={onPageChange}
          />
        </div>
      </div>
    </>
  );
}
